if (!customElements.get('infinite-journal-article')) {
  customElements.define('infinite-journal-article', class extends HTMLElement {
    connectedCallback() {
      this.content = this.querySelector('[data-journal-content]');
      this.progress = this.querySelector('[data-journal-progress]');
      this.toc = this.querySelector('[data-journal-toc]');
      this.tocLinks = this.querySelector('[data-journal-toc-links]');
      this.tocToggle = this.querySelector('[data-journal-toc-toggle]');
      this.copyButton = this.querySelector('[data-copy-link]');
      this.copyStatus = this.querySelector('[data-copy-status]');
      this.productFloat = this.querySelector('[data-journal-product-float]');
      this.productDismiss = this.querySelector('[data-journal-product-dismiss]');
      this.productForm = this.querySelector('[data-journal-product-form]');
      this.productAddButton = this.querySelector('[data-journal-product-add]');
      this.productCartStatus = this.querySelector('[data-journal-cart-status]');
      this.productStorageKey = this.productFloat
        ? `ilJournalProductDismissed:${this.productFloat.dataset.articleId || window.location.pathname}:${this.productFloat.dataset.productHandle || 'product'}`
        : '';
      try {
        this.productDismissed = Boolean(this.productStorageKey && window.sessionStorage.getItem(this.productStorageKey) === '1');
      } catch (error) {
        this.productDismissed = false;
      }

      if (this.content) {
        this.prepareTables();
        this.preparePromotions();
        this.prepareToc();
      }

      this.onScroll = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.updateProgress();
          this.updateToc();
          this.updateProductFloat();
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });

      this.onCopy = () => this.copyLink();
      if (this.copyButton) this.copyButton.addEventListener('click', this.onCopy);
      this.onTocToggle = () => {
        const isOpen = this.toc.classList.toggle('is-open');
        this.tocToggle.setAttribute('aria-expanded', String(isOpen));
        this.tocToggle.querySelector('span').textContent = isOpen ? '−' : '+';
      };
      if (this.tocToggle) this.tocToggle.addEventListener('click', this.onTocToggle);
      this.onTocLink = () => {
        if (window.matchMedia('(max-width: 749px)').matches && this.toc.classList.contains('is-open')) this.onTocToggle();
      };
      if (this.tocLinks) this.tocLinks.addEventListener('click', this.onTocLink);
      this.onProductDismiss = () => {
        this.productDismissed = true;
        try {
          window.sessionStorage.setItem(this.productStorageKey, '1');
        } catch (error) {
          // Dismissal still works when session storage is unavailable.
        }
        this.setProductFloat(false);
      };
      if (this.productDismiss) this.productDismiss.addEventListener('click', this.onProductDismiss);
      this.onProductSubmit = event => this.addProductToCart(event);
      if (this.productForm) this.productForm.addEventListener('submit', this.onProductSubmit);
      this.setProductFloat(false);
      this.onScroll();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      if (this.copyButton) this.copyButton.removeEventListener('click', this.onCopy);
      if (this.tocToggle) this.tocToggle.removeEventListener('click', this.onTocToggle);
      if (this.tocLinks) this.tocLinks.removeEventListener('click', this.onTocLink);
      if (this.productDismiss) this.productDismiss.removeEventListener('click', this.onProductDismiss);
      if (this.productForm) this.productForm.removeEventListener('submit', this.onProductSubmit);
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    prepareTables() {
      this.content.querySelectorAll('table').forEach(table => {
        if (table.parentElement && table.parentElement.classList.contains('il-journal-table-scroll')) return;
        const wrapper = document.createElement('div');
        wrapper.className = 'il-journal-table-scroll';
        wrapper.setAttribute('role', 'region');
        wrapper.setAttribute('aria-label', 'Scrollable data table');
        wrapper.tabIndex = 0;
        table.parentNode.insertBefore(wrapper, table);
        wrapper.appendChild(table);
      });
    }

    preparePromotions() {
      const productCard = this.querySelector('[data-journal-product-card]');
      const productSlot = this.content.querySelector('.il-article-product-slot, [data-journal-product-slot]');
      const legacyBanners = [...this.content.querySelectorAll('img')].filter(image => {
        const width = image.naturalWidth || Number(image.getAttribute('width')) || 0;
        const height = image.naturalHeight || Number(image.getAttribute('height')) || 0;
        const ratio = height ? width / height : 0;
        const signal = `${image.alt || ''} ${image.src || ''}`.toLowerCase();
        const linksToProduct = Boolean(image.closest('a[href*="/products/"]'));
        return (ratio >= 4 && linksToProduct) || signal.includes('buy-now') || signal.includes('banner-ad');
      });

      let cardPlaced = false;
      if (productCard && productSlot) {
        productSlot.replaceWith(productCard);
        productCard.hidden = false;
        cardPlaced = true;
      }
      legacyBanners.forEach(image => {
        let target = image.closest('a') || image;
        const wrapper = target.parentElement;
        if (wrapper && ['P', 'DIV'].includes(wrapper.tagName) && wrapper.children.length === 1 && !wrapper.textContent.trim()) target = wrapper;

        if (productCard && !cardPlaced) {
          target.replaceWith(productCard);
          productCard.hidden = false;
          cardPlaced = true;
        } else {
          target.remove();
        }
      });

      if (productCard && !cardPlaced) {
        const paragraphs = [...this.content.querySelectorAll('p')].filter(paragraph => paragraph.textContent.trim());
        const anchor = paragraphs[Math.min(1, Math.max(paragraphs.length - 1, 0))];
        if (anchor) anchor.insertAdjacentElement('afterend', productCard);
        else this.content.insertAdjacentElement('afterbegin', productCard);
        productCard.hidden = false;
      }
      if (!productCard && productSlot) productSlot.remove();
    }

    prepareToc() {
      let headings = [...this.content.querySelectorAll('h2')];
      if (headings.length < 2) headings = [...this.content.querySelectorAll('h2, h3')];
      this.headings = headings
        .filter(heading => heading.textContent.trim() && !heading.closest('[data-journal-product-card]'))
        .slice(0, 10);
      if (!this.toc || !this.tocLinks || this.headings.length < 2) return;

      const usedIds = new Set([...document.querySelectorAll('[id]')].map(element => element.id));
      this.headings.forEach((heading, index) => {
        if (!heading.id) {
          const base = this.slugify(heading.textContent) || `section-${index + 1}`;
          let candidate = base;
          let suffix = 2;
          while (usedIds.has(candidate)) candidate = `${base}-${suffix++}`;
          heading.id = candidate;
          usedIds.add(candidate);
        }

        const link = document.createElement('a');
        link.href = `#${heading.id}`;
        link.dataset.index = String(index + 1).padStart(2, '0');
        link.textContent = heading.textContent.trim();
        this.tocLinks.appendChild(link);
      });
      this.links = [...this.tocLinks.querySelectorAll('a')];
      this.toc.hidden = false;
    }

    slugify(value) {
      return value
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 72);
    }

    updateProgress() {
      if (!this.progress || !this.content) return;
      const start = this.content.getBoundingClientRect().top + window.scrollY;
      const length = Math.max(this.content.offsetHeight - window.innerHeight * .55, 1);
      const value = Math.min(1, Math.max(0, (window.scrollY - start + 140) / length));
      this.readingProgress = value;
      this.progress.style.transform = `scaleX(${value})`;
    }

    updateToc() {
      if (!this.links || !this.headings || !this.headings.length) return;
      const current = [...this.headings].reverse().find(heading => heading.getBoundingClientRect().top <= 180) || this.headings[0];
      this.links.forEach(link => {
        if (link.hash === `#${current.id}`) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
    }

    setProductFloat(show) {
      if (!this.productFloat) return;
      this.productFloat.hidden = false;
      this.productFloat.classList.toggle('is-visible', show);
      this.productFloat.toggleAttribute('inert', !show);
      this.productFloat.setAttribute('aria-hidden', String(!show));
    }

    updateProductFloat() {
      if (!this.productFloat) return;
      const articleEnd = this.querySelector('.il-journal-next');
      const endIsNear = articleEnd ? articleEnd.getBoundingClientRect().top <= window.innerHeight * .92 : false;
      this.setProductFloat(!this.productDismissed && (this.readingProgress || 0) >= .5 && !endIsNear);
    }

    async addProductToCart(event) {
      event.preventDefault();
      if (!this.productForm || !this.productAddButton || this.productCartBusy || !this.productForm.reportValidity()) return;
      this.productCartBusy = true;
      const originalLabel = this.productAddButton.dataset.defaultLabel || 'Add to cart';
      this.productAddButton.disabled = true;
      this.productAddButton.setAttribute('aria-busy', 'true');
      this.productAddButton.textContent = 'Adding…';
      if (this.productCartStatus) this.productCartStatus.textContent = '';

      const root = window.Shopify?.routes?.root || '/';
      const data = new FormData(this.productForm);
      data.set('sections', 'mini-cart');
      data.set('sections_url', window.location.pathname);
      try {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          body: data,
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        const result = await response.json();
        if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : 'This item could not be added.');

        this.productAddButton.textContent = 'Added ✓';
        if (this.productCartStatus) this.productCartStatus.textContent = 'Added to your cart.';
        this.productForm.dispatchEvent(new CustomEvent('variant:added', { bubbles: true, detail: { variant: result } }));
        if (result.sections?.['mini-cart']) {
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: { cart: result, openMiniCart: window.themeVariables?.settings?.cartType === 'drawer' }
          }));
        }
        try {
          const cart = await (await fetch(`${root}cart.js`)).json();
          document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));
        } catch (error) {
          // The add succeeded; a delayed cart-count refresh should not invite a duplicate submission.
        }
        if (window.themeVariables?.settings?.cartType === 'page') window.location.assign(`${root}cart`);
      } catch (error) {
        this.productAddButton.textContent = originalLabel;
        if (this.productCartStatus) this.productCartStatus.textContent = error.message || 'Unable to add this item. Please try again.';
      } finally {
        this.productCartBusy = false;
        this.productAddButton.removeAttribute('aria-busy');
        this.productAddButton.disabled = false;
        if (this.productAddButton.textContent === 'Added ✓') {
          window.setTimeout(() => {
            if (this.productAddButton) this.productAddButton.textContent = originalLabel;
          }, 1800);
        }
      }
    }

    async copyLink() {
      let copied = false;
      try {
        await navigator.clipboard.writeText(window.location.href);
        copied = true;
      } catch (error) {
        const input = document.createElement('textarea');
        input.value = window.location.href;
        input.setAttribute('readonly', '');
        input.style.position = 'fixed';
        input.style.opacity = '0';
        document.body.appendChild(input);
        input.select();
        copied = document.execCommand('copy');
        input.remove();
      }
      if (this.copyStatus) this.copyStatus.textContent = copied ? 'Article link copied.' : 'Could not copy the link.';
      if (copied && this.copyButton) {
        this.copyButton.dataset.copied = 'true';
        window.setTimeout(() => delete this.copyButton.dataset.copied, 1800);
      }
    }
  });
}
